import ArrowLeftOutlined from "@ant-design/icons/ArrowLeftOutlined"
import { Button, Col, Flex, Form, Input, Row, theme } from "antd"
import Spinner from "components/common/Spinner"
import useGeocoder from "modules/google/hooks/useGeocoder"
import useValidateAddress from "modules/google/hooks/useValidateAddress"
import PropTypes from "prop-types"
import { useState } from "react"
import { CountrySelector } from "ui/Input/CountrySelector"
import { Confirmed } from "ui/Icon/DSIcon/Confirmed"
const { useToken } = theme

export const LocationInfo = ({
  form,
  t,
  setManualAddress,
  setCenterLatLng,
  setMeetingLatLng,
  screenName,
  isRequired = true,
  backBtnManualText,
  onAddressConfirmed
}) => {
  const geocoder = useGeocoder(null)
  const { token } = useToken()
  const [isButtonDisabled, setIsButtonDisabled] = useState(true)
  const [buttonLabel, setButtonLabel] = useState(t("listings.tabs.location.confirm_address")) // Initial label

  const backBtnText = backBtnManualText || t("listings.tabs.location.back_to_search")
  const { handleConfirmAddress, loading } = useValidateAddress(
    geocoder,
    form,
    setCenterLatLng,
    setMeetingLatLng,
    setIsButtonDisabled,
    setButtonLabel
  )
  // Handle form value changes
  const handleValuesChange = () => {
    setIsButtonDisabled(false) // Enable the button when any input changes
    setButtonLabel(t("listings.tabs.location.confirm_address")) // Reset label to "Confirm Address"
  }

  // Handle back to search changes
  const handleBack = () => {
    if (setCenterLatLng) {
      setCenterLatLng(null)
    }
    setMeetingLatLng(null)
    setManualAddress(false)
    form.resetFields(["street_address", "city", "state", "zip_code", "country"]) // Reset the form fields
    setButtonLabel(t("listings.tabs.location.confirm_address")) // Reset label to "Confirm Address"
  }

  // Handle confirm address click
  const handleConfirmClick = async () => {
    try {
      await form.validateFields() // This will throw if any fields are invalid
      setButtonLabel(t("listings.tabs.location.confirming")) // Set label to "Confirming..."
      const response = await handleConfirmAddress()
      setButtonLabel(response ? t("listings.tabs.location.confirmed") : t("listings.tabs.location.confirm_address"))
      if (response && typeof onAddressConfirmed === "function") onAddressConfirmed()
    } catch (error) {
      // Validation failed
      console.error(error)
      setButtonLabel(t("listings.tabs.location.confirm_address")) // Reset to default
    }
  }

  const renderButtonLabel = () => {
    if (buttonLabel === "Confirmed") {
      return (
        <Flex align="center" justify="center">
          <Confirmed className="mr-s" />
          {buttonLabel}
        </Flex>
      )
    }
    return buttonLabel
  }

  return (
    <>
      <button
        className="d-flex align-items-center mb-l"
        onClick={handleBack}
        style={{
          background: "none",
          border: "none",
          padding: 0,
          display: "flex",
          alignItems: "center",
          cursor: "pointer"
        }}
      >
        <ArrowLeftOutlined style={{ color: token.colorPrimary, marginRight: "8px" }} />
        <span className="mb-0" style={{ color: token.colorPrimary }}>
          {backBtnText}
        </span>
      </button>

      <Form form={form} layout="vertical" onValuesChange={handleValuesChange} requiredMark={false}>
        <Form.Item
          name="street_address"
          label={
            <span>
              {t("listings.tabs.location.address.street_address")} {isRequired && <span>*</span>}
            </span>
          }
          rules={[{ required: isRequired, message: t("listings.tabs.location.validation.street_address") }]}
        >
          <Input
            placeholder={t("listings.tabs.location.address.street_address")}
            onBlur={(e) => form.setFieldValue("street_address", e.target.value.trim())}
          />
        </Form.Item>

        <Row gutter={16}>
          <Col span={screenName === "Client" ? "12" : "8"}>
            <Form.Item
              name="city"
              label={
                <span>
                  {t("listings.tabs.location.address.city")} {isRequired && <span>*</span>}
                </span>
              }
              rules={[{ required: isRequired, message: t("listings.tabs.location.validation.city") }]}
            >
              <Input
                placeholder={t("listings.tabs.location.address.city")}
                onBlur={(e) => form.setFieldValue("city", e.target.value.trim())}
              />
            </Form.Item>
          </Col>
          <Col span={screenName === "Client" ? "12" : "8"}>
            <Form.Item
              name="state"
              label={
                <span>
                  {t("listings.tabs.location.address.state")} {isRequired && <span>*</span>}
                </span>
              }
              rules={[{ required: isRequired, message: t("listings.tabs.location.validation.state") }]}
            >
              <Input
                placeholder={t("listings.tabs.location.address.state")}
                onBlur={(e) => form.setFieldValue("state", e.target.value.trim())}
              />
            </Form.Item>
          </Col>
          <Col span={screenName === "Client" ? "12" : "8"}>
            <Form.Item
              name="zip_code"
              label={
                <span>
                  {t("listings.tabs.location.address.zip_code")} {isRequired && <span>*</span>}
                </span>
              }
              rules={[
                { required: isRequired, message: t("listings.tabs.location.validation.zip_code") },
                {
                  pattern: /^\d{5}(-\d{4})?$/,
                  message: t("listings.tabs.location.validation.invalid_zip_code") // You can translate this string
                }
              ]}
            >
              <Input
                placeholder={t("listings.tabs.location.address.zip_code")}
                onBlur={(e) => form.setFieldValue("zip_code", e.target.value.trim())}
              />
            </Form.Item>
          </Col>
          <Col span={screenName === "Client" ? "12" : "24"} xl={true ? "6" : ""}>
            <Form.Item
              label={
                <span>
                  {t("listings.tabs.location.address.country")} {isRequired && <span>*</span>}
                </span>
              }
              name="country"
              required={isRequired}
              rules={[{ required: isRequired, message: t("listings.tabs.location.validation.country") }]}
            >
              <CountrySelector />
            </Form.Item>
          </Col>
        </Row>

        <Button type="primary" size="large" className="px-6" disabled={isButtonDisabled || loading} onClick={handleConfirmClick}>
          {loading ? (
            <Flex>
              <Spinner className="mr-s" color="white" size="small" />
              {t("listings.tabs.location.confirming")}
            </Flex>
          ) : (
            renderButtonLabel()
          )}
        </Button>
      </Form>
    </>
  )
}

LocationInfo.propTypes = {
  form: PropTypes.object,
  t: PropTypes.func,
  setManualAddress: PropTypes.func,
  setCenterLatLng: PropTypes.func,
  setMeetingLatLng: PropTypes.func,
  screenName: PropTypes.string,
  isRequired: PropTypes.bool,
  backBtnManualText: PropTypes.string,
  onAddressConfirmed: PropTypes.func
}
